import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { findDOMNode } from 'react-dom';
import { MDText } from 'i18n-react';
import List from 'react-virtualized/dist/commonjs/List';
import CellMeasurer, { CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer';

import SearchBox from '@adsk/bim360-matrix-react-search-box';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Loader from '@adsk/bim360-matrix-react-loader';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import Hr from '@adsk/bim360-matrix-react-hr';

import {
  accountProjectPickerWidth,
  accountProjectPickerListHeight,
  defaultAccountProjectPickerRowHeight
} from '@adsk/bim360-matrix-react-helpers/es/consts';
import escapeRegexString from '@adsk/bim360-matrix-react-helpers/es/escapeRegexString';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './_AccountProjectPicker.scss';

import tick from '@adsk/bim360-matrix-react-assets/tick.svg';
import accountProfileIcon from '@adsk/bim360-matrix-react-assets/account-profile.svg';
import projectProfileIcon from '@adsk/bim360-matrix-react-assets/project-profile.svg';
import noProjectsIcon from '@adsk/bim360-matrix-react-assets/no-projects.svg';
import backArrow from '@adsk/bim360-matrix-react-assets/back-arrow.svg';
import bim360Mark from '@adsk/bim360-matrix-react-assets/bim360-mark.svg';
import accMark from '@adsk/bim360-matrix-react-assets/acc-mark.svg';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';

/**
 * A Project Picker component for showing Project/Account list in MatrixHeader
 **/
export class AccountProjectPicker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchExpression: '',
      showAccountList: false,
      focused: false,
      isScrolled: false,
      projectsPerAccount: this.getProjectsPerAccount(props.projects),
      selectedAccount: this.getSelectedAccount(props),
      selectedProject: props.selectedProject,
      pickerListHeight: accountProjectPickerListHeight
    };

    this.focusProps = {
      role: 'button',
      tabIndex: '0'
    };
    this.HEADER_HEIGHT = 47;
    this._projectsCellCache = new CellMeasurerCache({
      fixedWidth: true,
      // This is different than the default height because of the header which could be less than 60
      minHeight: 30,
      defaultHeight: defaultAccountProjectPickerRowHeight,
      defaultWidth: accountProjectPickerWidth
    });

    this._accountsCellCache = new CellMeasurerCache({
      fixedWidth: true,
      // This is different than the default height because of the header which could be less than 60
      minHeight: 30,
      defaultHeight: defaultAccountProjectPickerRowHeight,
      defaultWidth: accountProjectPickerWidth
    });

    this.renderCurrentAccount = this.renderCurrentAccount.bind(this);
    this.renderProjectItem = this.renderProjectItem.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.setPickerListHeight = this.setPickerListHeight.bind(this);
    this.isProjectInCurrentAccount = this.isProjectInCurrentAccount.bind(this);
    this.getSelectedAccountByProject = this.getSelectedAccountByProject.bind(this);
  }

  isProjectInCurrentAccount(project) {
    return !!this.getProjectsByCurrentAccount().find(accountProject => accountProject === project);
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextState) {
    let propsToChange = {};
    if (nextProps.projects !== this.props.projects) {
      propsToChange = {
        ...propsToChange,
        projectsPerAccount: this.getProjectsPerAccount(nextProps.projects),
        selectedAccount: this.getSelectedAccount(nextProps)
      };
    }

    if (nextProps.selectedProject !== this.state.selectedProject) {
      if (
        this.isProjectInCurrentAccount(nextProps.selectedProject) ||
        this.state.searchExpression
      ) {
        propsToChange = { ...propsToChange, selectedProject: nextProps.selectedProject };
      }
    }

    if (nextProps.selectedAccount !== this.props.selectedAccount) {
      propsToChange = {
        ...propsToChange,
        selectedProject: null,
        selectedAccount: this.getSelectedAccount(nextProps)
      };
    }

    this.setState(propsToChange);

    if (
      nextProps.projects !== this.props.projects ||
      nextProps.selectedAccount !== this.props.selectedAccount
    ) {
      this._projectsCellCache.clearAll();
    }

    if (nextProps.accounts !== this.props.accounts) {
      this._accountsCellCache.clearAll();
    }

    if (nextProps.projectsLoadingError || this.props.projectsLoadingError) {
      this._projectsCellCache.clear(0, 0);
    }

    if (
      this.getProjectsByCurrentAccount().length === 0 &&
      (nextProps.isLoadingProjects !== this.props.isLoadingProjects ||
        nextProps.projectsLoadingError !== this.props.projectsLoadingError)
    ) {
      this._projectsCellCache.clear(1, 0);
    }
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-find-dom-node
    this.accountsListDomNode = findDOMNode(this.accountsList);
    // eslint-disable-next-line react/no-find-dom-node
    this.projectsListDomNode = findDOMNode(this.projectsList);
    this.setContainerHeight(this.state);
    this.setPickerListHeight();
    window.addEventListener('resize', this.setPickerListHeight);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setPickerListHeight);
  }

  getProjectsByCurrentAccount() {
    return (
      (this.state.projectsPerAccount[this.state.selectedAccount.id] &&
        this.state.projectsPerAccount[this.state.selectedAccount.id].projects) ||
      []
    );
  }

  handleScroll({ scrollTop }) {
    this.setState({ isScrolled: scrollTop > 0 });
  }

  componentDidUpdate() {
    setTimeout(() => {
      this.setContainerHeight(this.state);
    });
  }

  setPickerListHeight() {
    // 112px =
    //   MatrixHeader_top-panel(Margin 10px) +
    //   MatrixHeader__title-bar(height 40px) +
    //   AccountProjectPicker(Margin 15px) +
    //   AccountProjectPicker__search-box-container or AccountProjectPicker__account-view-header (Height 47px)
    const avaliableHeightOnViewport =
      Math.max(window.innerHeight || 0, document.documentElement.clientHeight) - 112;
    this.setState({
      pickerListHeight: Math.min(accountProjectPickerListHeight, avaliableHeightOnViewport)
    });
  }

  setContainerHeight(nextState) {
    const updateState = heightToUpdate => {
      if (this.state.innerContainerHeight !== heightToUpdate) {
        this.setState({ innerContainerHeight: heightToUpdate });
      }
    };

    if (this.innerContainer) {
      if (nextState.showAccountList && this.accountsListDomNode) {
        const heightToUpdate = Math.min(
          this.accountsListDomNode.scrollHeight,
          this.state.pickerListHeight
        );
        updateState(heightToUpdate);
      } else if (!nextState.showAccountList && this.projectsListDomNode) {
        const heightToUpdate = Math.min(
          this.projectsListDomNode.scrollHeight,
          this.state.pickerListHeight
        );
        updateState(heightToUpdate);
      }
    }
  }

  getProjectsPerAccount(projects) {
    const projectsPerAccount = {};
    projects.forEach(project => {
      if (!projectsPerAccount[project.accountId]) {
        projectsPerAccount[project.accountId] = {
          id: project.accountId,
          name: project.accountName,
          projects: []
        };
      }
      projectsPerAccount[project.accountId].projects.push(project);
    });
    return projectsPerAccount;
  }

  getSelectedAccountByProject({ accounts, selectedProject }) {
    return (
      accounts.find(account => account.id === selectedProject.accountId) || {
        id: selectedProject.accountId,
        name: selectedProject.accountName
      }
    );
  }

  getSelectedAccount(props) {
    return (
      props.selectedAccount || (props.selectedProject && this.getSelectedAccountByProject(props))
    );
  }

  renderAccountItem(inHeader) {
    return (account, style) => {
      let amountOfProjects = 0;

      if (!isNullOrUndefined(account.projectCount)) {
        amountOfProjects = account.projectCount;
      } else {
        if (this.state.projectsPerAccount[account.id]) {
          amountOfProjects = this.state.projectsPerAccount[account.id].projects.length;
        }
      }

      amountOfProjects = amountOfProjects < 9999 ? amountOfProjects : '9999+';

      const isSelectedAccount = account =>
        this.state.showAccountList &&
        this.state.selectedAccount &&
        this.state.selectedAccount.id === account.id;

      const itemClasses = classNames('AccountProjectPicker__menu-item', {
        'AccountProjectPicker__menu-item--selected': isSelectedAccount(account)
      });
      const itemNameClasses = classNames('AccountProjectPicker__menu-item-name', {
        'AccountProjectPicker__menu-item-name--selected': isSelectedAccount(account)
      });
      const itemSubtitleClasses = classNames('AccountProjectPicker__menu-item-subtitle', {
        'AccountProjectPicker__menu-item-subtitle--selected': isSelectedAccount(account)
      });
      const changeAccountClasses = classNames('AccountProjectPicker__change-account', {
        'AccountProjectPicker__change-account--selected': isSelectedAccount(account)
      });
      return (
        <div
          className={itemClasses}
          {...this.getFocusableProps(!inHeader)}
          style={style}
          onKeyPress={e => e.key === 'Enter' && this.props.onAccountClick(account)}
          onClick={() => {
            this.props.onAccountClick(account);
            if (this.props.showProjectListWhenSelectAccount) {
              this.setShowAccountList(false);
            }
          }}
        >
          <div className="AccountProjectPicker__account-icon">
            {account.imageUrl ? (
              <img src={account.imageUrl} alt="" />
            ) : (
              <SvgIcon width="100px" height="50px">
                {accountProfileIcon}
              </SvgIcon>
            )}
          </div>
          <div>
            <div className={itemNameClasses}>{account.name}</div>
            <div className="AccountProjectPicker__change-account-container">
              <div className={itemSubtitleClasses}>
                {amountOfProjects}{' '}
                {amountOfProjects === 1
                  ? this.props[TSymbolProp].translate('PROJECT')
                  : this.props[TSymbolProp].translate('PROJECTS')}
              </div>
              {inHeader && (
                <AnchorButton
                  className={changeAccountClasses}
                  onClick={event => {
                    event.stopPropagation();
                    this.setShowAccountList(true);
                  }}
                >
                  <div className="AccountProjectPicker__change-account-enlarged-anchor" />
                  {this.props[TSymbolProp].translate('CHANGE_ACCOUNT')}
                </AnchorButton>
              )}
            </div>
          </div>
          {isSelectedAccount(account) && (
            <span className="AccountProjectPicker__account-tick-icon">
              <SvgIcon width="9px" height="7px">
                {tick}
              </SvgIcon>
            </span>
          )}
        </div>
      );
    };
  }

  renderNoProjectsEmptyState() {
    return (
      <div className="AccountProjectPicker__projects-empty-state">
        <SvgIcon width="135px" height="125px">
          {noProjectsIcon}
        </SvgIcon>
        <div className="AccountProjectPicker__projects-empty-state-text">
          {this.props[TSymbolProp].translate('NO_PROJECTS')}
        </div>
        <div className="AccountProjectPicker__projects-empty-state-footer">
          <div>{this.props[TSymbolProp].translate('NO_PROJECTS_FOOTER')}</div>
          <div>
            <AnchorButton
              className="AccountProjectPicker__projects-empty-state-change-account"
              onClick={event => {
                event.stopPropagation();
                this.setShowAccountList(true);
              }}
            >
              {this.props[TSymbolProp].translate('CHANGE_ACCOUNT')}
            </AnchorButton>{' '}
            {this.props[TSymbolProp].translate('NO_PROJECTS_FOOTER_SEARCH_AT_TOP')}
          </div>
        </div>
      </div>
    );
  }

  renderCurrentAccount(style) {
    if (this.state.searchExpression) {
      return null;
    }
    const account = this.state.selectedAccount;
    const accountToRender = account || {
      id: this.state.selectedProject && this.state.selectedProject.accountId,
      name: this.state.selectedProject && this.state.selectedProject.accountName
    };
    return (
      <div className="AccountProjectPicker__current-account" style={style}>
        {this.renderAccountItem(true)(accountToRender, {})}
        <div className="AccountProjectPicker__current-account-sub-header">
          <span className="AccountProjectPicker__subtitle">
            {this.props[TSymbolProp].translate('PROJECTS')}
          </span>
          <Hr />
        </div>
      </div>
    );
  }

  renderSearchHeader(inErrorState, numOfProjects, style) {
    if (numOfProjects === 0) {
      if (inErrorState) {
        return <div style={style} />;
      }
      return (
        <div className="AccountProjectPicker__search-header-no-projects" style={style}>
          {this.props[TSymbolProp].translate('NO_PROJECTS_FOUND')}
        </div>
      );
    }
    const foundText = this.props[TSymbolProp].translate(
      numOfProjects === 1 ? 'PROJECT_FOUND' : 'PROJECTS_FOUND',
      { numOfProjects }
    );
    return (
      <div className="AccountProjectPicker__search-header" style={style}>
        {foundText}
      </div>
    );
  }

  renderFooter(isProjects) {
    return style => {
      let innerFooter;

      if (
        (isProjects && this.props.projectsLoadingError) ||
        (!isProjects && this.props.accountsLoadingError)
      ) {
        let localizationKey;
        if (isProjects) {
          localizationKey =
            this.props.projects.length === 0
              ? 'COULD_NOT_LOAD_PROJECTS'
              : 'COULD_NOT_LOAD_MORE_PROJECTS';
        } else {
          localizationKey =
            this.props.accounts.length === 0
              ? 'COULD_NOT_LOAD_ACCOUNTS'
              : 'COULD_NOT_LOAD_MORE_ACCOUNTS';
        }
        innerFooter = (
          <div>
            <span className="AccountProjectPicker__loading-error-text">
              {this.props[TSymbolProp].translate(localizationKey)}
            </span>
            <AnchorButton
              className={'AccountProjectPicker__footer-try-again'}
              onClick={() => {
                this.props.onTryLoadingAgainClick();
              }}
            >
              {this.props[TSymbolProp].translate('TRY_AGAIN')}
            </AnchorButton>
          </div>
        );
      } else if (
        (isProjects && this.props.isLoadingProjects) ||
        (!isProjects && this.props.isLoadingAccounts)
      ) {
        innerFooter = (
          <div className="AccountProjectPicker__loader-wrapper">
            <Loader className="AccountProjectPicker__loader" />
            <span className="AccountProjectPicker__loader-text">
              {this.props[TSymbolProp].translate('LOADING')}
            </span>{' '}
          </div>
        );
      } else if (isProjects && this.getProjectsByCurrentAccount().length === 0) {
        return (
          <div style={style} className="AccountProjectPicker__footer-empty-state">
            {this.renderNoProjectsEmptyState()}
          </div>
        );
      }

      return (
        <div style={style} className="AccountProjectPicker__footer">
          {innerFooter}
        </div>
      );
    };
  }

  renderProjectsVirtualized() {
    const accountId = this.state.selectedProject
      ? this.state.selectedProject.accountId
      : this.props.selectedAccount.id;

    const projectsFilter = project => {
      if (this.state.searchExpression) {
        const searchExpression = this.state.searchExpression.toLowerCase();
        return project.name.toLowerCase().indexOf(searchExpression) > -1;
      }
      return project.accountId === accountId;
    };

    const projects = this.props.projects.filter(projectsFilter);
    const renderHeader = this.state.searchExpression
      ? style => this.renderSearchHeader(this.props.projectsLoadingError, projects.length, style)
      : this.renderCurrentAccount;
    const rowRender = this._rowRenderer(
      this._projectsCellCache,
      projects,
      this.renderProjectItem,
      renderHeader,
      this.renderFooter(true)
    );
    const shouldShowEmptyState =
      !this.state.searchExpression &&
      this.getProjectsByCurrentAccount().length === 0 &&
      !this.props.isLoadingProjects &&
      !this.props.projectsLoadingError;
    const shouldShowFooter =
      this.props.isLoadingProjects || this.props.projectsLoadingError || shouldShowEmptyState;
    const rowCount = shouldShowFooter ? projects.length + 2 : projects.length + 1;
    const classes = classNames('AccountProjectPicker__virtual-list', {
      'AccountProjectPicker__virtual-list--empty-state': shouldShowEmptyState
    });

    return (
      <div className="AccountProjectPicker__project-container">
        {this.renderProjectsHeader()}
        <List
          ref={_ => (this.projectsList = _)}
          tabIndex={null}
          className={classes}
          deferredMeasurementCache={this._projectsCellCache}
          height={this.state.pickerListHeight}
          autoHeight={projects.length < 10}
          overscanRowCount={10}
          rowCount={rowCount}
          rowHeight={this._projectsCellCache.rowHeight}
          rowRenderer={rowRender}
          width={accountProjectPickerWidth}
          onScroll={this.handleScroll}
          style={{ maxHeight: this.state.pickerListHeight }}
        />
      </div>
    );
  }

  renderAccountsVirtualized() {
    const accounts = this.props.accounts;
    const renderHeader = style => (
      <div className="AccountProjectPicker__account-subtitle" style={style}>
        {this.props[TSymbolProp].translate('ACCOUNTS')}
      </div>
    );
    const rowRender = this._rowRenderer(
      this._accountsCellCache,
      accounts,
      this.renderAccountItem(false),
      renderHeader,
      this.renderFooter(false)
    );
    const shouldShowFooter = this.props.isLoadingAccounts || this.props.accountsLoadingError;
    const rowCount = shouldShowFooter ? accounts.length + 2 : accounts.length + 1;
    return (
      <div className="AccountProjectPicker__account-container">
        {this.renderAccountHeader()}
        <List
          ref={_ => (this.accountsList = _)}
          tabIndex={null}
          className="AccountProjectPicker__virtual-list"
          deferredMeasurementCache={this._accountsCellCache}
          height={this.state.pickerListHeight}
          autoHeight={accounts.length < 10}
          overscanRowCount={10}
          rowCount={rowCount}
          rowHeight={this._accountsCellCache.rowHeight}
          rowRenderer={rowRender}
          width={accountProjectPickerWidth}
          style={{ maxHeight: this.state.pickerListHeight }}
        />
      </div>
    );
  }

  _rowRenderer(cache, list, renderItem, renderHeader, renderFooter) {
    return ({ index, key, parent, style }) => {
      let row;
      if (index === 0) {
        row = renderHeader(style);
      } else if (index === list.length + 1) {
        row = renderFooter(style);
      } else {
        row = renderItem(list[index - 1], style);
      }

      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
          {row}
        </CellMeasurer>
      );
    };
  }

  _renderHighlightedTitle(searchExpression, title) {
    const splittedText = title.split(
      new RegExp('(' + escapeRegexString(searchExpression) + ')', 'ig')
    );
    return splittedText.map((partOfTitle, index) => {
      return (
        <span
          key={index}
          className={
            partOfTitle.toLowerCase() === searchExpression
              ? 'AccountProjectPicker__menu-item-highlighted-search'
              : ''
          }
        >
          {partOfTitle}
        </span>
      );
    });
  }

  getFocusableProps(forAccountView = false) {
    return forAccountView === this.state.showAccountList ? this.focusProps : {};
  }

  getProductIconFromItem = item => {
    return item.acsUnified ? accMark : bim360Mark;
  };

  renderProjectItem(item, style) {
    const { theme } = this.props;
    const isAlloy = theme === Themes.ALLOY;

    const isSelectedProject = item =>
      this.state.selectedProject && item.id === this.state.selectedProject.id;

    const accountProjectPickerMenuItemClass = 'AccountProjectPicker__menu-item';
    const itemClasses = classNames(accountProjectPickerMenuItemClass, {
      'AccountProjectPicker__menu-item--selected': isSelectedProject(item)
    });
    const itemNameClasses = classNames('AccountProjectPicker__menu-item-name', {
      'AccountProjectPicker__menu-item-name--selected': isSelectedProject(item)
    });
    const itemSubtitleClasses = classNames('AccountProjectPicker__menu-item-subtitle', {
      'AccountProjectPicker__menu-item-subtitle--selected': isSelectedProject(item)
    });

    const onProjectClicked = () => {
      this.setState({
        selectedProject: item,
        selectedAccount: this.getSelectedAccountByProject({ ...this.props, selectedProject: item })
      });
      this.props.onProjectClick(item);
    };

    const itemName = this.state.searchExpression
      ? this._renderHighlightedTitle(this.state.searchExpression.toLowerCase(), item.name)
      : item.name;
    return (
      <div
        style={style}
        {...this.getFocusableProps()}
        data-testid={accountProjectPickerMenuItemClass}
        key={item.id}
        className={itemClasses}
        onKeyPress={e => e.key === 'Enter' && onProjectClicked()}
        onClick={onProjectClicked}
      >
        <div className="AccountProjectPicker__project-icon">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt="" />
          ) : (
            <SvgIcon width="50px" height="50px">
              {projectProfileIcon}
            </SvgIcon>
          )}
        </div>
        <div className="AccountProjectPicker__project-content">
          <div className={itemNameClasses}>{itemName}</div>
          <div className={itemSubtitleClasses}>{item.accountName}</div>
        </div>
        {isAlloy && (
          <div className="AccountProjectPicker__product-icon">
            <SvgIcon width="16px" height="16px">
              {this.getProductIconFromItem(item)}
            </SvgIcon>
          </div>
        )}
        {isAlloy && (
          <span className="AccountProjectPicker__tick-icon">
            {isSelectedProject(item) && (
              <SvgIcon width="9px" height="7px">
                {tick}
              </SvgIcon>
            )}
          </span>
        )}
        {!isAlloy && isSelectedProject(item) && (
          <span className="AccountProjectPicker__tick-icon">
            <SvgIcon width="9px" height="7px">
              {tick}
            </SvgIcon>
          </span>
        )}
      </div>
    );
  }

  handleSearchChange(searchExpression) {
    this._projectsCellCache.clearAll();
    this.setState({ searchExpression });
  }

  renderProjectsHeader() {
    const classes = classNames('AccountProjectPicker__search-box', {
      'AccountProjectPicker__search-box--focused': this.state.focused
    });
    const containerClasses = classNames('AccountProjectPicker__search-box-container', {
      'AccountProjectPicker__search-box-container--shadowed': this.state.isScrolled
    });

    return (
      <div className={containerClasses} style={{ height: this.HEADER_HEIGHT }}>
        <SearchBox
          searchExpression={this.state.searchExpression}
          onInputRefMount={ref => (this.searchInput = ref)}
          isFocused={true}
          disableInputFocusIndication={true}
          className={classes}
          placeholder={this.props[TSymbolProp].translate('SEARCH_PLACEHOLDER')}
          onChange={text => {
            this.handleSearchChange(text);
          }}
          onSearch={() => {}}
          onFocus={() => {
            this.setState({ focused: true });
          }}
          onBlur={() => {
            this.setState({ focused: false });
          }}
          onClear={() => {
            this.handleSearchChange('');
          }}
          clearDisabled={false}
          searchDisabled={false}
          inputDisabled={false}
        />
      </div>
    );
  }

  renderAccountHeader() {
    return (
      <div
        className="AccountProjectPicker__account-view-header"
        style={{ height: this.HEADER_HEIGHT }}
      >
        {
          <AnchorButton
            tabIndex={this.state.showAccountList ? 0 : -1}
            className="AccountProjectPicker__back-to-projects"
            onClick={() => {
              this.setShowAccountList(false);
            }}
          >
            <SvgIcon width="9px" height="13px">
              {backArrow}
            </SvgIcon>
            <span>{this.props[TSymbolProp].translate('BACK_TO_PROJECTS')}</span>
          </AnchorButton>
        }
        <Hr />
      </div>
    );
  }

  setShowAccountList(showAccountList) {
    this.setState({ showAccountList }, () => {
      if (!showAccountList) {
        if (this.searchInput && this.searchInput.focus) {
          this.searchInput.focus();
        }
      }
    });
    if (showAccountList) {
      this.accountsList.scrollToRow(0);
    } else {
      this.projectsList.scrollToRow(0);
    }
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const classes = classNames('AccountProjectPicker', this.props.className || '');
    const innerContainer = classNames('AccountProjectPicker__inner-container', {
      'AccountProjectPicker__inner-container--translated': this.state.showAccountList
    });

    // Adding this extraHeight to add in case we have a scroll
    const extraHeight = this.state.innerContainerHeight === this.state.pickerListHeight ? 2 : 0;
    // Since the innerContainerHeight is based on the account/project list, we need to compensate for the header height.
    const height = `${this.state.innerContainerHeight + this.HEADER_HEIGHT + extraHeight}px`;

    return (
      <div className={classes} style={this.props.style}>
        <div
          className="AccountProjectPicker__container"
          onClick={event => {
            event.stopPropagation();
          }}
        >
          <div className={innerContainer} style={{ height }} ref={_ => (this.innerContainer = _)}>
            <div className="AccountProjectPicker__content">
              {this.renderProjectsVirtualized()}
              {this.renderAccountsVirtualized()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

AccountProjectPicker.accountShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  projectCount: PropTypes.number,
  imageUrl: PropTypes.string,
  idleTimeout: PropTypes.shape({ enabled: PropTypes.bool, duration: PropTypes.number })
};

AccountProjectPicker.projectShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  accountName: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string })).isRequired,
  imageUrl: PropTypes.string
};

AccountProjectPicker.propTypes = {
  /**
   * A custom className for the component container
   */
  className: PropTypes.string,
  /**
   * Custom style object for the component container
   */
  style: PropTypes.object,
  /**
   * Callback for clicking on project. onProjectClick(EntitlementProject) : void
   */
  onProjectClick: PropTypes.func.isRequired,
  /**
   * Callback for clicking on account. onAccountClick(EntitlementAccount) : void
   */
  onAccountClick: PropTypes.func.isRequired,
  /**
   * Callback for clicking 'try again' to load more accounts/projects in case projectsLoadingError or accountLoadingError is true. onTryLoadingAgainClick() : void
   */
  onTryLoadingAgainClick: PropTypes.func.isRequired,
  /**
   * Selected project from the list of projects
   */
  selectedProject: PropTypes.shape(AccountProjectPicker.projectShape),
  /**
   * Selected account from the list of accounts
   */
  selectedAccount: PropTypes.shape(AccountProjectPicker.accountShape),
  /**
   * An array of projects to show when in project level mode of the picker
   */
  projects: PropTypes.arrayOf(PropTypes.shape(AccountProjectPicker.projectShape)),
  /**
   * An array of accounts to show when in accounts level mode of the picker
   */
  accounts: PropTypes.arrayOf(PropTypes.shape(AccountProjectPicker.accountShape)),
  /**
   * Whether projects are still loading
   */
  isLoadingProjects: PropTypes.bool,
  /**
   * Whether accounts are still loading
   */
  isLoadingAccounts: PropTypes.bool,
  /**
   * Whether there was an error loading accounts
   */
  accountsLoadingError: PropTypes.bool,
  /**
   * Whether there was an error loading projects
   */
  projectsLoadingError: PropTypes.bool,
  /**
   * The locale to use for this component
   */
  locale: PropTypes.string,
  /**
   * Show project lists when user select an account
   */
  showProjectListWhenSelectAccount: PropTypes.bool,
  theme: PropTypes.oneOf(objectValues(Themes))
};

AccountProjectPicker.defaultProps = {
  projects: [],
  accounts: [],
  showProjectListWhenSelectAccount: false
};

export default withLocale(
  AccountProjectPicker,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
